/* eslint-disable max-lines */
import { debounce } from 'lodash';
import React, { useCallback, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { API_URL } from 'src/api';
import { fetcher } from 'src/utils/fetch';

type RecordingState = 'idle' | 'recording' | 'recorded' | 'transcribing';

const FUZZY_MATCH_PREVIEW_QUERY = `
  query FuzzyMatchPreview($name: String!, $threshold: Float, $trigramWeight: Float, $phoneticAlgorithm: String, $stringAlgorithm: String) {
    fuzzyMatchPreview(name: $name, threshold: $threshold, trigramWeight: $trigramWeight, phoneticAlgorithm: $phoneticAlgorithm, stringAlgorithm: $stringAlgorithm) {
      candidates {
        userId
        displayName
        confidence
      }
      isAmbiguous
    }
  }
`;

type StringAlgorithm = 'trigram' | 'jaro-winkler' | 'levenshtein' | 'dice';

const STRING_ALGORITHM_OPTIONS: {
  value: StringAlgorithm;
  label: string;
  description: string;
}[] = [
  {
    value: 'trigram',
    label: 'Trigram (pg_trgm)',
    description: 'DB-native — uses the GIN index; fast at scale',
  },
  {
    value: 'jaro-winkler',
    label: 'Jaro-Winkler',
    description:
      'Handles transpositions; favours common prefixes — good for names',
  },
  {
    value: 'levenshtein',
    label: 'Levenshtein',
    description:
      'Edit distance (normalised) — counts insertions, deletions, substitutions',
  },
  {
    value: 'dice',
    label: 'Dice Coefficient',
    description: 'Bigram overlap — similar to trigrams but less granular',
  },
];

type PhoneticAlgorithm =
  | 'double-metaphone'
  | 'metaphone'
  | 'soundex'
  | 'soundex-dm';

const ALGORITHM_OPTIONS: {
  value: PhoneticAlgorithm;
  label: string;
  description: string;
}[] = [
  {
    value: 'double-metaphone',
    label: 'Double Metaphone',
    description: 'Primary + secondary codes — best recall for English names',
  },
  {
    value: 'metaphone',
    label: 'Metaphone',
    description: 'Single code — simpler than Double Metaphone',
  },
  {
    value: 'soundex',
    label: 'Soundex',
    description: 'Classic algorithm — groups similar-sounding names',
  },
  {
    value: 'soundex-dm',
    label: 'Daitch-Mokotoff Soundex',
    description: 'Variant tuned for Slavic and Jewish surnames',
  },
];

interface Candidate {
  userId: number;
  displayName: string;
  confidence: number;
}

interface FuzzyMatchPreviewData {
  fuzzyMatchPreview: {
    candidates: Candidate[];
    isAmbiguous: boolean;
  };
}

const FuzzyMatchPreview: React.FC = () => {
  const [name, setName] = useState('');
  const [debouncedName, setDebouncedName] = useState('');
  const [threshold, setThreshold] = useState(0.75);
  const [trigramWeight, setTrigramWeight] = useState(0.7);
  const [phoneticAlgorithm, setPhoneticAlgorithm] =
    useState<PhoneticAlgorithm>('double-metaphone');
  const [stringAlgorithm, setStringAlgorithm] =
    useState<StringAlgorithm>('trigram');

  // Recording state
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [transcribeError, setTranscribeError] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobEvent['data'][]>([]);

  const startRecording = async () => {
    try {
      setTranscribeError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        setRecordingState('recorded');
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecordingState('recording');
    } catch {
      setTranscribeError('Microphone access denied');
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
  };

  const submitTranscription = async () => {
    if (!audioBlob) return;
    setRecordingState('transcribing');
    setTranscribeError(null);
    try {
      // Decode the browser audio (WebM/Ogg/etc.) to raw 16-bit mono PCM at
      // 16 kHz — the only lossless path to AWS Transcribe Streaming without S3
      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioCtx = new AudioContext({ sampleRate: 16000 });
      const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
      await audioCtx.close();

      // Mix down to mono and convert Float32 → Int16
      const float32 = audioBuffer.getChannelData(0);
      const int16 = new Int16Array(float32.length);
      for (let i = 0; i < float32.length; i++) {
        int16[i] = Math.max(
          -32768,
          Math.min(32767, Math.round(float32[i] * 32767)),
        );
      }

      const formData = new FormData();
      formData.append(
        'audio',
        new Blob([int16.buffer], { type: 'audio/pcm' }),
        'recording.pcm',
      );
      const r = await fetch(`${API_URL}/fuzzy-match-preview/transcribe`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      if (!r.ok) {
        const err = (await r.json()) as { error?: string };
        throw new Error(err.error ?? 'Transcription failed');
      }
      const { transcript } = (await r.json()) as { transcript: string };
      setName(transcript);
      debouncedSet(transcript);
      setRecordingState('idle');
      setAudioBlob(null);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    } catch (err) {
      setTranscribeError(
        err instanceof Error ? err.message : 'Transcription failed',
      );
      setRecordingState('recorded');
    }
  };

  const resetRecording = () => {
    mediaRecorderRef.current?.stop();
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setTranscribeError(null);
    setRecordingState('idle');
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSet = useCallback(
    debounce((value: string) => setDebouncedName(value), 300),
    [],
  );

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    debouncedSet(e.target.value);
  };

  const { data, isFetching } = useQuery<FuzzyMatchPreviewData>(
    [
      'fuzzyMatchPreview',
      debouncedName,
      threshold,
      trigramWeight,
      phoneticAlgorithm,
      stringAlgorithm,
    ],
    fetcher<
      FuzzyMatchPreviewData,
      {
        name: string;
        threshold: number;
        trigramWeight: number;
        phoneticAlgorithm: string;
        stringAlgorithm: string;
      }
    >(FUZZY_MATCH_PREVIEW_QUERY, {
      name: debouncedName,
      threshold,
      trigramWeight,
      phoneticAlgorithm,
      stringAlgorithm,
    }),
    { enabled: debouncedName.trim().length > 0, keepPreviousData: true },
  );

  const result = data?.fuzzyMatchPreview;

  return (
    <div style={{ padding: '32px', maxWidth: '800px' }}>
      <h2 style={{ marginBottom: '8px' }}>Fuzzy Match Preview</h2>
      <div
        style={{
          color: '#555',
          marginBottom: '24px',
          fontSize: '14px',
          lineHeight: '1.6',
        }}
      >
        <p style={{ marginBottom: '8px' }}>
          POC tool for evaluating the named-offender fuzzy matching service
          before full integration. Use it to test how different algorithm
          combinations perform against your organisation&apos;s employee
          directory.
        </p>
        <ol style={{ margin: '0 0 8px 16px', padding: 0 }}>
          <li>
            <strong>Enter a name</strong> by typing in the field below, or click{' '}
            <strong>Start recording</strong> to speak a name and let AWS
            Transcribe convert it to text automatically.
          </li>
          <li>
            <strong>Pick algorithms</strong> — choose a string matching
            algorithm (Trigram, Jaro-Winkler, etc.) and a phonetic algorithm
            (Double Metaphone, Soundex, etc.) to compare their behaviour.
          </li>
          <li>
            <strong>Tune the weights</strong> using the slider to shift
            confidence between the string signal and the phonetic signal.
          </li>
          <li>
            <strong>Adjust the threshold</strong> to see how many candidates
            pass at different confidence levels. Watch for the ambiguous match
            warning when two candidates score too closely.
          </li>
        </ol>
        <p style={{ margin: 0, color: '#999' }}>
          Only visible when <code>fuzzy-matching-named-offenders</code> is
          enabled for this org.
        </p>
      </div>

      <div
        style={{
          marginBottom: '16px',
          padding: '12px 16px',
          background: '#f9f9f9',
          border: '1px solid #e0e0e0',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: '13px', color: '#555', fontWeight: 600 }}>
          🎙 VaultTalk simulation
        </span>

        {recordingState === 'idle' && (
          <button
            onClick={startRecording}
            style={{
              padding: '6px 14px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              background: '#fff',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            Start recording
          </button>
        )}

        {recordingState === 'recording' && (
          <>
            <span
              style={{
                fontSize: '13px',
                color: '#d32f2f',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#d32f2f',
                  display: 'inline-block',
                  animation: 'pulse 1s infinite',
                }}
              />
              Recording…
            </span>
            <button
              onClick={stopRecording}
              style={{
                padding: '6px 14px',
                borderRadius: '4px',
                border: '1px solid #d32f2f',
                background: '#ffebee',
                cursor: 'pointer',
                fontSize: '13px',
                color: '#d32f2f',
              }}
            >
              Stop
            </button>
          </>
        )}

        {(recordingState === 'recorded' || recordingState === 'transcribing') &&
          audioUrl && (
            <>
              <audio src={audioUrl} controls style={{ height: '32px' }} />
              <button
                onClick={submitTranscription}
                disabled={recordingState === 'transcribing'}
                style={{
                  padding: '6px 14px',
                  borderRadius: '4px',
                  border: '1px solid #1976d2',
                  background: '#e3f2fd',
                  cursor:
                    recordingState === 'transcribing' ? 'default' : 'pointer',
                  fontSize: '13px',
                  color: '#1976d2',
                  opacity: recordingState === 'transcribing' ? 0.7 : 1,
                }}
              >
                {recordingState === 'transcribing'
                  ? 'Transcribing…'
                  : 'Transcribe'}
              </button>
              {recordingState !== 'transcribing' && (
                <button
                  onClick={resetRecording}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    background: '#fff',
                    cursor: 'pointer',
                    fontSize: '12px',
                    color: '#666',
                  }}
                >
                  ✕ Discard
                </button>
              )}
            </>
          )}

        {transcribeError && (
          <span style={{ fontSize: '12px', color: '#d32f2f' }}>
            {transcribeError}
          </span>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '24px',
          alignItems: 'flex-end',
        }}
      >
        <div style={{ flex: 1 }}>
          <label
            htmlFor="fuzzy-name-input"
            style={{
              display: 'block',
              marginBottom: '4px',
              fontSize: '13px',
              fontWeight: 600,
            }}
          >
            Name to match
          </label>
          <input
            id="fuzzy-name-input"
            type="text"
            value={name}
            onChange={handleNameChange}
            placeholder="e.g. Jon Smyth"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
              boxSizing: 'border-box',
            }}
          />
        </div>
        <div>
          <label
            htmlFor="fuzzy-threshold-input"
            style={{
              display: 'block',
              marginBottom: '4px',
              fontSize: '13px',
              fontWeight: 600,
            }}
          >
            Threshold
          </label>
          <input
            id="fuzzy-threshold-input"
            type="number"
            value={threshold}
            onChange={(e) => setThreshold(Number(e.target.value))}
            min={0}
            max={1}
            step={0.01}
            style={{
              width: '90px',
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
            }}
          />
        </div>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <label
          htmlFor="fuzzy-weight-slider"
          style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          Signal weights
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '13px', minWidth: '130px' }}>
            {
              STRING_ALGORITHM_OPTIONS.find((o) => o.value === stringAlgorithm)
                ?.label
            }
            : <strong>{trigramWeight.toFixed(2)}</strong>
          </span>
          <input
            id="fuzzy-weight-slider"
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={trigramWeight}
            onChange={(e) => setTrigramWeight(Number(e.target.value))}
            style={{ flex: 1 }}
          />
          <span
            style={{ fontSize: '13px', minWidth: '140px', textAlign: 'right' }}
          >
            {
              ALGORITHM_OPTIONS.find((o) => o.value === phoneticAlgorithm)
                ?.label
            }
            : <strong>{(1 - trigramWeight).toFixed(2)}</strong>
          </span>
        </div>
        <p style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
          Confidence ={' '}
          {
            STRING_ALGORITHM_OPTIONS.find((o) => o.value === stringAlgorithm)
              ?.label
          }{' '}
          × {trigramWeight.toFixed(2)} +{' '}
          {ALGORITHM_OPTIONS.find((o) => o.value === phoneticAlgorithm)?.label}{' '}
          × {(1 - trigramWeight).toFixed(2)}
        </p>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <p style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>
          String matching algorithm
        </p>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {STRING_ALGORITHM_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setStringAlgorithm(opt.value)}
              title={opt.description}
              style={{
                padding: '6px 14px',
                borderRadius: '4px',
                border:
                  stringAlgorithm === opt.value
                    ? '2px solid #388e3c'
                    : '1px solid #ccc',
                background: stringAlgorithm === opt.value ? '#e8f5e9' : '#fff',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: stringAlgorithm === opt.value ? 600 : 400,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <p style={{ fontSize: '12px', color: '#999', marginTop: '6px' }}>
          {
            STRING_ALGORITHM_OPTIONS.find((o) => o.value === stringAlgorithm)
              ?.description
          }
        </p>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <p
          style={{
            marginBottom: '8px',
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          Phonetic algorithm
        </p>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {ALGORITHM_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setPhoneticAlgorithm(opt.value)}
              title={opt.description}
              style={{
                padding: '6px 14px',
                borderRadius: '4px',
                border:
                  phoneticAlgorithm === opt.value
                    ? '2px solid #1976d2'
                    : '1px solid #ccc',
                background:
                  phoneticAlgorithm === opt.value ? '#e3f2fd' : '#fff',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: phoneticAlgorithm === opt.value ? 600 : 400,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <p style={{ fontSize: '12px', color: '#999', marginTop: '6px' }}>
          {
            ALGORITHM_OPTIONS.find((o) => o.value === phoneticAlgorithm)
              ?.description
          }
        </p>
      </div>

      {isFetching && (
        <p style={{ color: '#999', fontSize: '14px' }}>Searching…</p>
      )}

      {!isFetching && debouncedName.trim().length > 0 && result && (
        <>
          {result.isAmbiguous && (
            <div
              style={{
                background: '#fff8e1',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                padding: '10px 14px',
                marginBottom: '16px',
                fontSize: '13px',
              }}
            >
              ⚠️ <strong>Ambiguous</strong> — multiple candidates with scores
              within 0.10 of each other. All would be excluded from routing (PRD
              safety principle).
            </div>
          )}

          {result.candidates.length === 0 ? (
            <p style={{ color: '#666', fontSize: '14px' }}>
              No matches above threshold ({threshold}).{' '}
              {result.candidates.length === 0 && !result.isAmbiguous
                ? 'The feature flag may also be off for this org.'
                : ''}
            </p>
          ) : (
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px',
              }}
            >
              <thead>
                <tr
                  style={{
                    background: '#f5f5f5',
                    borderBottom: '2px solid #ddd',
                  }}
                >
                  <th style={{ padding: '10px 16px', textAlign: 'left' }}>
                    Directory name
                  </th>
                  <th
                    style={{
                      padding: '10px 16px',
                      textAlign: 'right',
                      width: '120px',
                    }}
                  >
                    Confidence
                  </th>
                </tr>
              </thead>
              <tbody>
                {result.candidates.map((c) => (
                  <tr key={c.userId} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '10px 16px' }}>{c.displayName}</td>
                    <td
                      style={{
                        padding: '10px 16px',
                        textAlign: 'right',
                        fontFamily: 'monospace',
                        fontWeight: c.confidence >= 0.9 ? 600 : 400,
                      }}
                    >
                      {(c.confidence * 100).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {!isFetching && debouncedName.trim().length === 0 && (
        <p style={{ color: '#aaa', fontSize: '14px' }}>
          Enter a name above to search.
        </p>
      )}
    </div>
  );
};

export default FuzzyMatchPreview;
